import { DerbyRngV1 } from '../rng';
import {
  DRILL_TICK_S,
  MASK_HELD,
  type MinigameDef,
  type MinigameSession,
  type SessionScore,
} from './framework';

/**
 * Stamina @ the Endurance Trail — zone management. HOLD pushes the
 * heart-rate needle up (first-order lag); releasing lets it fall.
 * Terrain segments move the green band and tug the needle on their own;
 * keep it in the band. Pinned in the red too long and the horse blows up.
 */

export type TerrainKind = 'flat' | 'climb' | 'descent' | 'creek' | 'gust' | 'surge';

export interface TrailSegment {
  kind: TerrainKind;
  startTick: number;
  endTick: number;
  bandCenter: number;
}

export interface TrailTierConfig {
  durationS: number;
  bandHalf: number;
  kinds: TerrainKind[];
}

export function trailTier(tier: number): TrailTierConfig {
  switch (Math.max(1, Math.min(4, tier))) {
    case 1:
      return { durationS: 15, bandHalf: 0.18, kinds: ['flat', 'climb'] };
    case 2:
      return { durationS: 15, bandHalf: 0.14, kinds: ['flat', 'climb', 'descent', 'creek'] };
    case 3:
      return { durationS: 15, bandHalf: 0.11, kinds: ['flat', 'climb', 'descent', 'creek', 'gust'] };
    default:
      return {
        durationS: 15,
        bandHalf: 0.09,
        kinds: ['flat', 'climb', 'descent', 'creek', 'gust', 'surge'],
      };
  }
}

const BAND_CENTER: Record<TerrainKind, number> = {
  flat: 0.55,
  climb: 0.68,
  descent: 0.4,
  creek: 0.3,
  gust: 0.55,
  surge: 0.82,
};

/** Where the needle drifts with the reins loose — climbs push it up,
 * but never all the way into the band: idling is never good riding. */
const PASSIVE_TARGET: Record<TerrainKind, number> = {
  flat: 0.3,
  climb: 0.46,
  descent: 0.14,
  creek: 0.3,
  gust: 0.34,
  surge: 0.4,
};

const RED_ZONE = 0.93;
const RED_LIMIT_S = 1.5;
const RHYTHM_STREAK_S = 1.5;
const RHYTHM_MULT = 1.5;
const PERFECT_FRACTION = 0.9;

export type TrailEvent =
  | { kind: 'segment'; terrain: TerrainKind }
  | { kind: 'perfect' }
  | { kind: 'blown' }
  | { kind: 'done' };

export class TrailSession implements MinigameSession {
  readonly config: TrailTierConfig;
  readonly segments: TrailSegment[];
  readonly durationTicks: number;

  tick = 0;
  needle = 0.4;
  blown = false;
  private done = false;
  private weightedInBand = 0;
  private inBandStreakTicks = 0;
  private redTicks = 0;
  private segmentIndex = 0;
  private segmentInBand = 0;
  private segmentTicks = 0;
  private perfectSegments = 0;
  private readonly events: TrailEvent[] = [];

  constructor(tier: number, seed: number) {
    this.config = trailTier(tier);
    this.durationTicks = Math.round(this.config.durationS / DRILL_TICK_S);
    const rng = new DerbyRngV1(seed).fork('trail');
    const segments: TrailSegment[] = [];
    let at = 0;
    let previous: TerrainKind | null = null;
    while (at < this.durationTicks) {
      let kind = rng.pick(this.config.kinds);
      if (kind === previous) kind = rng.pick(this.config.kinds);
      const shortKind = kind === 'creek' || kind === 'surge';
      const lengthS = shortKind ? rng.range(1.2, 1.8) : rng.range(2, 3.2);
      const endTick = Math.min(this.durationTicks, at + Math.round(lengthS / DRILL_TICK_S));
      segments.push({ kind, startTick: at, endTick, bandCenter: BAND_CENTER[kind] });
      previous = kind;
      at = endTick;
    }
    this.segments = segments;
    this.events.push({ kind: 'segment', terrain: segments[0]!.kind });
  }

  get finished(): boolean {
    return this.done;
  }

  currentSegment(): TrailSegment {
    return this.segments[this.segmentIndex] ?? this.segments[this.segments.length - 1]!;
  }

  /** Band center at the current tick (gusts wobble it). */
  bandCenterNow(): number {
    const segment = this.currentSegment();
    if (segment.kind !== 'gust') return segment.bandCenter;
    return segment.bandCenter + Math.sin(this.tick * DRILL_TICK_S * 3.2) * 0.09;
  }

  get rhythmActive(): boolean {
    return this.inBandStreakTicks >= Math.round(RHYTHM_STREAK_S / DRILL_TICK_S);
  }

  drainEvents(): TrailEvent[] {
    return this.events.splice(0, this.events.length);
  }

  step(mask: number): void {
    if (this.done) return;
    const dt = DRILL_TICK_S;
    const held = (mask & MASK_HELD) !== 0;
    const segment = this.currentSegment();

    const target = held ? 1 : PASSIVE_TARGET[segment.kind];
    const tau = held ? 0.6 : 1.0;
    this.needle += ((target - this.needle) * dt) / tau;

    const inBand = Math.abs(this.needle - this.bandCenterNow()) <= this.config.bandHalf;
    if (inBand) {
      this.inBandStreakTicks += 1;
      this.weightedInBand += this.rhythmActive ? RHYTHM_MULT : 1;
      this.segmentInBand += 1;
    } else {
      this.inBandStreakTicks = 0;
    }
    this.segmentTicks += 1;

    if (this.needle > RED_ZONE) {
      this.redTicks += 1;
      if (this.redTicks >= Math.round(RED_LIMIT_S / DRILL_TICK_S)) {
        this.blown = true;
        this.done = true;
        this.events.push({ kind: 'blown' });
        return;
      }
    } else {
      this.redTicks = 0;
    }

    this.tick += 1;
    if (this.tick >= segment.endTick && this.segmentIndex < this.segments.length - 1) {
      if (this.segmentTicks > 0 && this.segmentInBand / this.segmentTicks >= PERFECT_FRACTION) {
        this.perfectSegments += 1;
        this.events.push({ kind: 'perfect' });
      }
      this.segmentIndex += 1;
      this.segmentInBand = 0;
      this.segmentTicks = 0;
      this.events.push({ kind: 'segment', terrain: this.currentSegment().kind });
    }
    if (this.tick >= this.durationTicks) {
      if (this.segmentTicks > 0 && this.segmentInBand / this.segmentTicks >= PERFECT_FRACTION) {
        this.perfectSegments += 1;
      }
      this.done = true;
      this.events.push({ kind: 'done' });
    }
  }

  score(): SessionScore {
    // Duration is the denominator: blowing up early forfeits the rest.
    const bandFraction = Math.min(1, this.weightedInBand / this.durationTicks);
    const perfectFraction = this.segments.length > 0 ? this.perfectSegments / this.segments.length : 0;
    const raw = Math.max(0, Math.min(1, 0.8 * bandFraction + 0.2 * perfectFraction));
    return {
      raw,
      detail: `${Math.round(bandFraction * 100)}% in rhythm · ${this.perfectSegments} perfect stretches${this.blown ? ' · blew up!' : ''}`,
      failed: this.blown,
    };
  }
}

export const TRAIL_DEF: MinigameDef = {
  id: 'trail',
  name: 'Endurance Trail',
  stat: 'stamina',
  energyCost: 4,
  create: (tier, seed) => new TrailSession(tier, seed),
};
